import { useTheme } from '../theme/ThemeContext';

type ApiListSkeletonProps = {
  /** Number of placeholder cards. */
  count?: number;
  /** Optional label announced to screen readers. */
  label?: string;
  className?: string;
};

/**
 * Sidebar list loading — same shimmer card language as HeatMapObservationBar
 * (blurred ghost bars + sweeping gradient).
 */
export default function ApiListSkeleton({
  count = 5,
  label = 'Loading…',
  className = '',
}: ApiListSkeletonProps) {
  const { isDark } = useTheme();
  const skeletonBase = isDark
    ? 'bg-[#1c2432] border-[#2d3748]'
    : 'bg-[#f3f4f6] border-[#e5e7eb]';
  const skeletonPulse = isDark ? 'bg-[#253347]' : 'bg-[#e5e7eb]';
  const via = isDark ? 'via-white/10' : 'via-white/55';

  return (
    <div
      className={`space-y-2 py-1 ${className}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={label}
    >
      <style>{`
        @keyframes apiListShimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
      {Array.from({ length: count }, (_, i) => {
        const delayMs = i * 90;
        return (
          <div
            key={i}
            className={`relative overflow-hidden rounded-[14px] border px-2.5 py-2.5 ${skeletonBase}`}
            style={{ animationDelay: `${delayMs}ms` }}
            aria-hidden
          >
            <div className="blur-[2.5px] opacity-70 select-none pointer-events-none flex items-start gap-3">
              <div className={`h-9 w-9 shrink-0 rounded-lg ${skeletonPulse}`} />
              <div className="min-w-0 flex-1 space-y-2 pt-0.5">
                <div className="flex items-center gap-2">
                  <div className={`h-3.5 w-[55%] max-w-[9rem] rounded ${skeletonPulse}`} />
                  <div className={`h-4 w-10 rounded-md ${skeletonPulse}`} />
                </div>
                <div className={`h-2.5 w-[70%] max-w-[11rem] rounded ${skeletonPulse}`} />
                <div className="flex items-center gap-2 pt-0.5">
                  <div className={`h-2 w-12 rounded ${skeletonPulse}`} />
                  <div className={`h-2 w-16 rounded ${skeletonPulse}`} />
                </div>
              </div>
            </div>
            <div
              className={`absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent to-transparent ${via}`}
              style={{
                animation: `apiListShimmer 1.35s ease-in-out ${delayMs}ms infinite`,
              }}
            />
          </div>
        );
      })}
      <span className="sr-only">{label}</span>
    </div>
  );
}
